import React__default from 'react';

/**
 * Provides routing utilities for various components.
 */
const RouterProvider = ({ navigate: propNavigate, getCurrentPropValue: propGetCurrentPropValue, basename, children, }) => {
    const navigate = React__default.useCallback((href) => propNavigate(normalizeHref(href, basename)), [propNavigate, basename]);
    const getCurrentPropValue = React__default.useCallback((href) => propGetCurrentPropValue?.(normalizeHref(href, basename)), [propGetCurrentPropValue, basename]);
    const handleClick = React__default.useCallback((e) => {
        if (e.target instanceof Element) {
            // From https://gist.github.com/devongovett/919dc0f06585bd88af053562fd7c41b7
            const link = e.target.closest('a');
            if (link &&
                link instanceof HTMLAnchorElement &&
                link.getAttribute('href') &&
                (!link.target || link.target === '_self') &&
                link.origin === location.origin &&
                !link.hasAttribute('download') &&
                e.button === 0 && // left clicks only
                !e.metaKey && // open in new tab (mac)
                !e.ctrlKey && // open in new tab (windows)
                !e.altKey && // download
                !e.shiftKey &&
                !e.defaultPrevented) {
                e.preventDefault();
                // We do check the existence of the attribute value above.
                // We use the direct attribute value instead of `link.href` in the gist because react-router-dom v5 can't handle full URLs.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                navigate(link.getAttribute('href'));
            }
        }
    }, [navigate]);
    return (React__default.createElement(RouterContext.Provider, { value: React__default.useMemo(() => ({ onClick: handleClick, getCurrentPropValue }), [handleClick, getCurrentPropValue]) }, children));
};
function normalizeHref(href, basename) {
    if (basename) {
        return href.startsWith(basename) ? href.substring(basename.length) : href;
    }
    return href;
}
const RouterContext = React__default.createContext(null);
/**
 * Enables using the provided routing utilities in components.
 */
const useRouter = ({ onClick } = {}) => {
    const ctx = React__default.useContext(RouterContext);
    const handleClick = React__default.useCallback((e) => {
        onClick?.(e);
        ctx?.onClick(e);
    }, [onClick, ctx]);
    return React__default.useMemo(() => ({
        onClick: handleClick,
        getCurrentPropValue: ctx?.getCurrentPropValue,
    }), [handleClick, ctx?.getCurrentPropValue]);
};

export { RouterProvider, useRouter };
